import type { Route } from '../components/Shell';

// Routes addressable by name alone. `alerts` accepts a prefill, but that never goes
// in the URL (see routeToHash), so it belongs here too.
const PARAMLESS_ROUTES = [
  'overview',
  'applications',
  'messagetypes',
  'alerts',
  'aitokens',
  'aiusage',
  'aimodels',
  'aiprices',
  'railwaycosts',
  'benchmark',
  'conversations',
  'audits',
  'notifications',
  'settings',
  'users',
  'versions',
] as const;

/**
 * The route lives in the URL hash, so a reload lands back where you were and the
 * browser's Back/Forward buttons move between views.
 *
 * Hash rather than path routing: it needs nothing from the server's static fallback or
 * the service worker's navigation fallback, both of which only know about index.html.
 *
 * `prefill` is deliberately left out. It is a one-click hint one view hands another,
 * not state worth restoring, and a URL carrying filter ids would outlive the ids.
 */
export function routeToHash(route: Route): string {
  switch (route.name) {
    case 'appdash': {
      const base = `#/appdash/${encodeURIComponent(route.applicationId)}`;
      return route.logId ? `${base}/log/${encodeURIComponent(route.logId)}` : base;
    }
    case 'aiappdetail':
      return `#/aiappdetail/${encodeURIComponent(route.applicationId)}`;
    case 'benchmarkdetail':
      return `#/benchmarkdetail/${encodeURIComponent(route.runId)}`;
    case 'auditdetail':
      return `#/auditdetail/${encodeURIComponent(route.id)}`;
    default:
      return `#/${route.name}`;
  }
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // A hand-mangled URL should land somewhere sensible, not throw during render.
    return segment;
  }
}

/** Parses a hash back into a route; anything unrecognised falls back to the overview. */
export function hashToRoute(hash: string): Route {
  const [name, first, second, third] = hash
    .replace(/^#\/?/, '')
    .split('/')
    .filter(Boolean)
    .map(decodeSegment);
  if (name === 'appdash' && first) {
    return second === 'log' && third
      ? { name, applicationId: first, logId: third }
      : { name, applicationId: first };
  }
  if (name === 'aiappdetail' && first) return { name, applicationId: first };
  if (name === 'benchmarkdetail' && first) return { name, runId: first };
  if (name === 'auditdetail' && first) return { name, id: first };
  const paramless = PARAMLESS_ROUTES.find((r) => r === name);
  return { name: paramless ?? 'overview' };
}
